/**
 * Tipos + constantes de la colección personal del usuario (`vocab_entries`),
 * la que la Biblioteca muestra en "Lo mío". El módulo nació como pantalla
 * propia (Vocabulario, `docs/speak-plan/08-vocabulary-module.md`); esa
 * pantalla se absorbió en Lo mío y se borró (card #348), y de acá quedan los
 * tipos, los nombres de los temas y las paletas del gestor de etiquetas.
 */

import type { VocabTopic } from '@orborix/shared-types';
import type { FamiliarityTier } from '../../components/familiarity/Familiarity.js';

export type VocabEntryType = 'word' | 'phrase' | 'phrasal_verb' | 'expression' | 'collocation';
export type VocabOrigin = 'user' | 'app';
export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

/** Forma que consumen los componentes (nombres cortos del diseño). */
export interface VocabEntry {
  id: string;
  en: string;
  es: string | null;
  ipa: string | null;
  pos: string | null;
  entryType: VocabEntryType;
  cefr: CefrLevel | null;
  origin: VocabOrigin;
  topic: VocabTopic | null;
  favorite: boolean;
  difficulty: boolean;
  example: string | null;
  particle: string | null;
  separable: boolean | null;
  /** La charla en la que el coach la detectó (`source_session_id`). */
  session: string | null;
  uses: number;
  familiarity: FamiliarityTier;
  tags: string[];
  /**
   * Está en el diccionario activo (`GET /vocab`, decisión 86): una palabra o
   * un verbo frasal como `give up`. Lo decide el backend por cada entrada.
   */
  inDictionary: boolean;
}

export interface VocabTag {
  id: string;
  name: string;
  emoji: string;
  color: string;
  sortOrder: number;
}

// ─── Temas (los pone el coach; lista cerrada en shared-types) ───────
// Un `Record` sobre `VocabTopic`: sumar un tema a la lista sin nombrarlo acá
// no compila.
export const VOCAB_TOPIC_META: Readonly<Record<VocabTopic, { label: string; color: string }>> = {
  nature: { label: 'Naturaleza', color: '#d4f23e' },
  travel: { label: 'Viajes', color: '#5ce1e6' },
  work: { label: 'Trabajo', color: '#a78bfa' },
  food: { label: 'Comida', color: '#ff9d6c' },
  social: { label: 'Social', color: '#ff6ec7' },
  technology: { label: 'Tecnología', color: '#8095ff' },
  family: { label: 'Familia', color: '#ffb863' },
  health: { label: 'Salud', color: '#5ee6a8' },
  home: { label: 'Casa', color: '#e6b85e' },
  leisure: { label: 'Ocio', color: '#c77dff' },
  shopping: { label: 'Compras', color: '#ff8f8f' },
  sports: { label: 'Deportes', color: '#34d399' },
  music: { label: 'Música', color: '#facc15' },
  entertainment: { label: 'Cine y series', color: '#e879f9' },
  learning: { label: 'Estudio', color: '#38bdf8' },
};

// ─── Etiquetas: paletas para el gestor ──────────────────────────────
export const TAG_EMOJIS = [
  '✈️', '💼', '❤️', '📝', '🎯', '🌍', '🍔', '🎬', '🏠', '⭐', '🔥', '💡', '📚', '🎓', '☕', '🗣️',
];
export const TAG_COLORS = ['#5ce1e6', '#a78bfa', '#ff6ec7', '#d4f23e', '#ff9d6c', '#8095ff'];
